// Canlı Durum — temsilcilerin anlık durumu (varsayılan sayfa).

import { useCallback, useEffect, useRef, useState } from 'react';
import { getLiveAgents, openLiveStream } from '../api';
import { brandLabel, HANDLING_IDLE_AFTER_MIN, INBOUND_DUTY_META, SHIFT_META, STATE_META, tzOfBrands } from '../constants';
import { chipHealth, type Level } from '../ext-health';
import { isOutdated, newestVersion } from '../ext-version';
import { fmtClock, fmtElapsed, fmtRange, fmtRel, fmtTalkLong } from '../format';
import { useNow } from '../hooks';
import type { AgentState, BrandId, Lang, LiveAgent, LiveOnCall } from '../types';
import { isOffDuty } from '../types';
import { dotStyle, EmptyState, Icon, Select, Skeleton } from '../ui';
import AgentCommandModal from './AgentCommandModal';
import BulkBar from './BulkBar';
import ExtHealthDrawer from './ExtHealthDrawer';

/** Sürüm çipinin sağlık rengi (tasarım A): gri = sorun yok, amber = eski sürüm / bugün uyarı, kırmızı = kayıp sonuç. */
const CHIP_COLOR: Record<Level, string | null> = { ok: null, none: null, warn: '#e0a23c', bad: '#ef5d5d' };
import LiveWeekStrip from './LiveWeekStrip';

const ORDER: Record<AgentState, number> = { working: 0, break: 1, offline: 2 };

// Yenileme ritmi (sahibin kararı, 2026-10): itme kanalı (SSE) sağlıklıyken yoklama yalnız
// mutabakattır → 30 sn; kanal yoksa/koptuysa 10 sn. Sekme gizliyken ikisi de DURUR.
const POLL_MS = 10_000;
const POLL_WITH_SSE_MS = 30_000;
// SSE yeniden bağlanma: düşen sağlıklı akış 1 sn'den başlayıp 30 sn'ye kadar ikiye katlanarak
// yeniden denenir; ART ARDA iki bağlantı denemesi hiç anlık görüntü alamadıysa yoklamaya düşülür
// ve SSE dakikada bir yeniden denenir.
const SSE_BACKOFF_MIN_MS = 1_000;
const SSE_BACKOFF_MAX_MS = 30_000;
const SSE_FALLBACK_RETRY_MS = 60_000;
const SSE_FAILS_BEFORE_FALLBACK = 2;

/** "Aramada" sayacının başlangıcı: `since` sunucu saatine çevrilmiş faz başlangıcıdır; yine de
 *  gelecekte kalıyorsa (düzeltilememiş saat) sunucunun alım anı `receivedAt` kullanılır. */
function onCallClock(c: LiveOnCall, now: number): string {
  const t = new Date(c.since).getTime();
  return Number.isFinite(t) && t > now + 2000 ? c.receivedAt : c.since;
}

/** Planda izinli/TL olanlar en alta iner: "çevrimdışı" gibi görünmeleri bir arıza
 *  sinyaliymiş izlenimi veriyordu — oysa o gün gelmeleri beklenmiyor. */
const sortRank = (a: LiveAgent) => (isOffDuty(a.schedule) ? 3 : ORDER[a.state]);

/** Metrik şeridindeki küçük başlıklar (ÇAĞRI / İLK–SON / TERMİNAL). */
const METRIC_LABEL: React.CSSProperties = {
  fontSize: '9px',
  fontWeight: 600,
  letterSpacing: '.09em',
  color: 'var(--faint)',
  lineHeight: 1,
};

export default function LiveStatus({
  brands,
  onGoAlerts,
  onGoAgent,
  isAdmin,
  lang = 'tr',
}: {
  brands: BrandId[];
  onGoAlerts: () => void;
  onGoAgent: (agentId: string) => void;
  isAdmin: boolean;
  lang?: Lang;
}) {
  // Eklenti Sağlığı çekmecesi (yalnız admin) — sürüm çipi ya da 🛟 ile açılır. Satır her canlı
  // güncellemede `liveAgents`'tan yeniden okunur, çekmece hep taze veriyi çizer.
  const [drawerId, setDrawerId] = useState<string | null>(null);
  // Toplu işlem (tasarım D): seçim modunda kart tıklaması detaya gitmez, seçer.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Sürüm çipi ipucu: kart overflow:hidden olduğu için sayfa düzeyinde, sabit konumda çizilir.
  const [tip, setTip] = useState<{ id: string; x: number; y: number } | null>(null);
  // Uzaktan destek hedefi. Karttaki can simidi düğmesiyle açılır; kartın kendi tıklaması
  // temsilci detayına gittiği için düğme olayı stopPropagation ile durdurur.
  const [cmdTarget, setCmdTarget] = useState<{ id: string; name: string; panelDark: boolean; lastBeatAt: string | null } | null>(null);
  const [liveAgents, setLiveAgents] = useState<LiveAgent[]>([]);
  const [liveLoading, setLiveLoading] = useState(true);
  const [liveUpdatedAt, setLiveUpdatedAt] = useState(0);
  const [liveDept, setLiveDept] = useState('all');
  const [liveSearch, setLiveSearch] = useState('');
  const alive = useRef(true);
  const now = useNow(1000);

  // Sekme gizliyken ne yoklama ne akış: arka plandaki bir panel sunucuya yük bindirmesin.
  const [hidden, setHidden] = useState(() => typeof document !== 'undefined' && document.hidden);
  // İtme kanalı şu an anlık görüntü teslim ediyor mu? true iken yoklama 30 sn'ye uzar.
  const [sseHealthy, setSseHealthy] = useState(false);

  const load = useCallback(async () => {
    setLiveLoading(true);
    let rows: LiveAgent[];
    try {
      rows = await getLiveAgents();
    } catch {
      // Geçici hata kartları boşaltmaz; bir sonraki yoklama/anlık görüntü düzeltir.
      if (alive.current) setLiveLoading(false);
      return;
    }
    if (!alive.current) return;
    setLiveAgents(rows);
    setLiveUpdatedAt(Date.now());
    setLiveLoading(false);
  }, []);

  useEffect(() => {
    alive.current = true;
    const onVis = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive.current = false;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // Görünür olunca (ilk açılış dahil) HEMEN tazele; sonra ritimli yoklama.
  useEffect(() => {
    if (hidden) return;
    void load();
  }, [hidden, load]);

  useEffect(() => {
    if (hidden) return;
    const id = setInterval(() => void load(), sseHealthy ? POLL_WITH_SSE_MS : POLL_MS);
    return () => clearInterval(id);
  }, [hidden, sseHealthy, load]);

  // SSE: görünürken açık, gizlenince kapalı. Hata = kanal kapatılır ve buradaki takvimle
  // yeniden denenir (tarayıcının kendi yeniden bağlanması kullanılmaz).
  useEffect(() => {
    if (hidden) {
      setSseHealthy(false);
      return;
    }
    let disposed = false;
    let stop: (() => void) | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = SSE_BACKOFF_MIN_MS;
    let failedConnects = 0;

    const connect = () => {
      if (disposed) return;
      let gotSnapshot = false;
      stop = openLiveStream({
        onSnapshot: (rows) => {
          if (disposed || !alive.current) return;
          gotSnapshot = true;
          failedConnects = 0;
          backoffMs = SSE_BACKOFF_MIN_MS;
          setLiveAgents(rows);
          setLiveUpdatedAt(Date.now());
          setLiveLoading(false);
          setSseHealthy(true);
        },
        onError: () => {
          if (disposed) return;
          stop = null;
          setSseHealthy(false);
          let delay = backoffMs;
          if (!gotSnapshot) failedConnects += 1;
          if (failedConnects >= SSE_FAILS_BEFORE_FALLBACK) {
            delay = SSE_FALLBACK_RETRY_MS; // yoklamaya düşüldü (10 sn); SSE dakikada bir denenir
          } else {
            backoffMs = Math.min(SSE_BACKOFF_MAX_MS, backoffMs * 2);
          }
          timer = setTimeout(connect, delay);
        },
      });
    };
    connect();
    return () => {
      disposed = true;
      if (timer) clearTimeout(timer);
      if (stop) stop();
      setSseHealthy(false);
    };
  }, [hidden]);

  const brandRows = liveAgents.filter((a) => a.brands.some((b) => brands.includes(b)));
  const q = liveSearch.trim().toLocaleLowerCase('tr');
  let liveRows = brandRows.slice();
  if (liveDept !== 'all') liveRows = liveRows.filter((a) => a.department === liveDept);
  if (q) liveRows = liveRows.filter((a) => a.name.toLocaleLowerCase('tr').includes(q));
  liveRows.sort((a, b) => sortRank(a) - sortRank(b) || a.name.localeCompare(b.name, 'tr'));

  // Özet kutuları filtreden değil, marka kapsamının tamamından hesaplanır.
  // İzinli/TL olanlar KENDİ kutusunda sayılır ve "çevrimdışı"dan düşülür — yoksa
  // planlı bir izin günü, ekranda kesinti gibi okunuyor.
  const offDutyRows = brandRows.filter((a) => isOffDuty(a.schedule));
  // Kart sürüm çipinin referansı: görünen TÜM temsilciler arasında en yeni eklenti sürümü (tek
  // eklenti tüm markalara hizmet eder, o yüzden marka filtresinden bağımsız).
  const newestExt = newestVersion(liveAgents.map((a) => a.extVersion || ''));
  const toggleSel = (id: string) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const drawerAgent = drawerId ? liveAgents.find((x) => x.id === drawerId) || null : null;
  const tipAgent = tip ? liveAgents.find((x) => x.id === tip.id) || null : null;
  const tipHealth = tipAgent ? chipHealth(tipAgent, isOutdated(tipAgent.extVersion || '', newestExt), newestExt, lang) : null;
  const dutyRows = brandRows.filter((a) => !isOffDuty(a.schedule));
  const sumWorking = dutyRows.filter((a) => a.state === 'working').length;
  const sumBreak = dutyRows.filter((a) => a.state === 'break').length;
  const sumOffline = dutyRows.filter((a) => a.state === 'offline').length;
  const sumOffDuty = offDutyRows.length;
  const showOffDuty = brandRows.some((a) => a.schedule !== null);
  const sumCalls = brandRows.reduce((n, a) => n + a.todayCalls, 0);
  // İzinli/TL bir temsilcinin çağrı yapması ARTIK sayılmaz (sahibin kararı, 2026-07-27):
  // uyarı kuralı emekliye ayrıldı, sayaç da onunla birlikte gitti — yoksa Uyarı kutusu
  // Uyarılar sayfasında karşılığı olmayan bir sayı gösterirdi.
  // Gelen çağrı görevindeki temsilci bu kutunun DIŞINDA: santralden gelen çağrılar
  // eklentiye uğramadığı için "çağrı yapmış ama şimdi çevrimdışı" tam olarak o görevin
  // normal görüntüsü — ve motor da o aralıkta uyarı üretmiyor (migration 015). Çalışıyor
  // /çevrimdışı sayaçları GÖZLEMSEL kalır, onlar dokunulmaz.
  const alertRows = dutyRows.filter((a) => !a.schedule?.inboundActive);
  const sumAlerts =
    alertRows.filter((a) => a.state === 'break' && a.breakRemainingMin < 0).length +
    alertRows.filter((a) => a.state === 'offline' && a.todayCalls > 0).length;

  // Departmanı atanmamış temsilci LİSTEDE görünür ama SEÇENEK üretmez: eskiden boş
  // departman '—' idi ve açılır menüye adsız bir satır olarak düşüyordu.
  const deptOptions = [{ value: 'all', label: 'Tüm departmanlar' }].concat(
    Array.from(new Set(brandRows.map((a) => a.department).filter(Boolean))).map((d) => ({ value: d, label: d })),
  );

  // Yoklama iskeleti göstermesin: yalnızca ilk yüklemede.
  const liveLoadingNow = liveLoading && liveAgents.length === 0;
  const liveUpdatedLabel = liveUpdatedAt
    // Yenileme damgası da kapsamın dilimindedir: kartların saatleri Akra iken başlıkta
    // İstanbul saati görmek "veri 3 saat bayat" gibi okunuyordu.
    ? fmtClock(liveUpdatedAt, tzOfBrands(brands), true)
    : '';
  const liveEmptyReason =
    brands.length === 0 ? 'Üstteki marka filtresinden en az bir marka seçin.' : 'Seçili filtrelerle eşleşen temsilci bulunamadı.';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 12px',
            background: 'rgba(55,196,107,.1)',
            border: '1px solid rgba(55,196,107,.3)',
            borderRadius: '999px',
          }}
        >
          <span
            style={{
              width: '7px',
              height: '7px',
              borderRadius: '999px',
              background: '#37c46b',
              animation: 'caaPulse 1.6s ease-in-out infinite',
            }}
          />
          <span style={{ fontSize: '11.5px', fontWeight: 600, letterSpacing: '.08em', color: '#37c46b' }}>CANLI</span>
        </div>
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: 'var(--faint)',
            fontSize: '12px',
          }}
        >
          {/* İlk yükleme bitene kadar etiket hiç basılmaz — saatsiz "Son güncelleme". */}
          {liveUpdatedLabel && (
            <span>
              Son güncelleme <span style={{ color: 'var(--dim)', fontVariantNumeric: 'tabular-nums' }}>{liveUpdatedLabel}</span>
            </span>
          )}
          <button
            onClick={() => void load()}
            title="Yenile"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '9px',
              color: 'var(--dim)',
              cursor: 'pointer',
            }}
          >
            <Icon name="refresh" size={18} />
          </button>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit,minmax(148px,1fr))',
          gap: '12px',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '14px 15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(55,196,107,.14)',
              flex: 'none',
            }}
          >
            <Icon name="headset_mic" size={20} color="#37c46b" />
          </span>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {sumWorking}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>Çalışıyor</div>
          </div>
        </div>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '14px 15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(224,162,60,.14)',
              flex: 'none',
            }}
          >
            <Icon name="local_cafe" size={20} color="#e0a23c" />
          </span>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {sumBreak}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>Molada</div>
          </div>
        </div>
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '14px 15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(154,164,178,.14)',
              flex: 'none',
            }}
          >
            <Icon name="power_settings_new" size={20} color="var(--dim)" />
          </span>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {sumOffline}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>Çevrimdışı</div>
          </div>
        </div>
        {showOffDuty && (
          <div
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              padding: '14px 15px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <span
              style={{
                width: '38px',
                height: '38px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(107,114,128,.14)',
                flex: 'none',
              }}
            >
              <Icon name="event_busy" size={20} color="var(--dim)" />
            </span>
            <div>
              <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                {sumOffDuty}
              </div>
              {/* Kutu offDutyRows'u sayar = shift 'off' VE 'tl'; sadece "İzinli" demek
                  takım liderlerini izinli gösteriyordu. Sayım değişmedi, etiket dürüstleşti. */}
              <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>İzinli / TL</div>
            </div>
          </div>
        )}
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '14px 15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <span
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(61,139,255,.14)',
              flex: 'none',
            }}
          >
            <Icon name="call" size={20} color="#3d8bff" />
          </span>
          <div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {sumCalls}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>Bugün toplam çağrı</div>
          </div>
        </div>
        <button
          onClick={onGoAlerts}
          style={{
            textAlign: 'left',
            background: 'var(--surface)',
            border: '1px solid rgba(239,93,93,.3)',
            borderRadius: '12px',
            padding: '14px 15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(239,93,93,.14)',
              flex: 'none',
            }}
          >
            <Icon name="warning" size={20} color="#ef5d5d" />
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
              {sumAlerts}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--dim)', marginTop: '3px' }}>Yeni uyarı</div>
          </div>
          <Icon name="chevron_right" size={18} color="#ef5d5d" />
        </button>
      </div>

      {/* Pazartesi 00:00'dan beri marka toplamları (görev #195/#196) — Haftalık Rapor'un cari haftası. */}
      <LiveWeekStrip brands={brands} hidden={hidden} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <Select value={liveDept} onChange={setLiveDept} options={deptOptions} />
        <div style={{ position: 'relative', flex: 1, minWidth: '200px', maxWidth: '320px' }}>
          <Icon
            name="search"
            size={18}
            color="var(--faint)"
            style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
          />
          <input
            value={liveSearch}
            onChange={(e) => setLiveSearch(e.target.value)}
            placeholder="Temsilci ara…"
            style={{
              width: '100%',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '10px',
              padding: '10px 13px 10px 38px',
              color: 'var(--text)',
              fontSize: '13px',
              outline: 'none',
            }}
          />
        </div>
        <div style={{ marginLeft: 'auto', fontSize: '12.5px', color: 'var(--dim)', whiteSpace: 'nowrap' }}>
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{liveRows.length}</span> / {brandRows.length} temsilci
        </div>
        {/* Toplu işlem (tasarım D, yalnız admin): seçim modunda kart tıklaması seçer. */}
        {isAdmin && (
          <button
            onClick={() => { setSelectMode((v) => !v); setSelected(new Set()); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '7px', padding: '7px 11px', borderRadius: '8px',
              fontSize: '12px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
              ...(selectMode
                ? { border: '1px solid var(--accent-border)', background: 'var(--accent-soft)', color: 'var(--accent-text)' }
                : { border: '1px solid var(--border)', background: 'transparent', color: 'var(--dim)' }),
            }}
          >
            <Icon name="checklist" size={16} />
            <span>Seçim modu</span>
          </button>
        )}
      </div>

      {liveLoadingNow && <Skeleton heights={[60, 150, 60]} />}

      {!liveLoadingNow && liveRows.length === 0 && (
        <EmptyState icon="search_off" title="Temsilci bulunamadı" reason={liveEmptyReason} minHeight={220} />
      )}

      {!liveLoadingNow && liveRows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '14px' }}>
          {liveRows.map((a) => {
            const meta = STATE_META[a.state];
            const isBreak = a.state === 'break';
            const over = isBreak && a.breakRemainingMin < 0; // negatif kalan süre = mola aşımı
            const sch = a.schedule;
            const offDuty = isOffDuty(sch);
            // Gelen çağrı görevi vardiya rozetinin YERİNE geçer: saat aralığı aynı kalır,
            // ikon ve renk "bu kişi santralden çağrı alıyor" der. Görev yoksa hiçbir şey
            // değişmez (INBOUND_DUTY_META[''] tanımsız).
            const shiftMeta = sch
              ? (INBOUND_DUTY_META[sch.inboundDuty] || SHIFT_META[sch.shift])
              : null;
            // Kartın saatleri TEMSİLCİNİN dilimindedir (BetBooker=Akra) — İlk–Son ve
            // Terminal'i sunucu zaten öyle üretiyor; mola saati de aynı dilimi kullanmalı.
            const tz = tzOfBrands(a.brands);
            // `AwaitingOutcome` bu iş akışında dakikalarla ölçülür; bu eşiği aşan bir kart
            // "görüşme" değil PARK EDİLMİŞ bir paneldir (müşteri açık bırakılıp gidilmiş).
            // Aynı veriyi farklı okuruz: etiket ve renk değişir, satır yine tek satırdır.
            const handlingMs = a.handlingSince ? now - new Date(a.handlingSince).getTime() : 0;
            const handlingIdle = handlingMs >= HANDLING_IDLE_AFTER_MIN * 60_000;
            const extVer = a.extVersion || '';
            const extOld = isOutdated(extVer, newestExt);
            const chipH = chipHealth(a, extOld, newestExt, lang);
            const chipC = CHIP_COLOR[chipH.level];
            const isSel = selectMode && selected.has(a.id);
            return (
              // Kart tıklanabilir: temsilci detayına atlar. <button> DEĞİL — tarayıcının
              // buton UA stili flex hizasını bozar (piksel sadakati); rol+tabIndex+Enter/Space
              // ile klavye erişimi aynı şekilde sağlanır.
              <div
                key={a.id}
                role="button"
                tabIndex={0}
                data-caa-clickcard
                onClick={() => (selectMode ? toggleSel(a.id) : onGoAgent(a.id))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (selectMode) toggleSel(a.id);
                    else onGoAgent(a.id);
                  }
                }}
                title={selectMode ? undefined : 'Temsilci detayını aç'}
                style={{
                  position: 'relative',
                  overflow: 'hidden',
                  background: isSel ? 'var(--selected)' : 'var(--surface)',
                  border: '1px solid ' + (isSel ? 'rgba(61,139,255,.6)' : 'var(--border)'),
                  borderRadius: '14px',
                  padding: '14px 16px 11px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '11px',
                  cursor: 'pointer',
                }}
              >
                {isBreak && (
                  // Molada halkası: kartın kendi kenarlığının ÜSTÜNE çizilir, yer kaplamaz.
                  // Durum bilgisi alt satırda olduğu için molada kart Çalışıyor kartıyla
                  // AYNI yükseklikte kalır — ızgarada satır germesi olmaz.
                  <span
                    style={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '14px',
                      border: '1px solid ' + (over ? 'rgba(239,93,93,.35)' : 'rgba(224,162,60,.3)'),
                      pointerEvents: 'none',
                    }}
                  />
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                    {selectMode ? (
                      <span style={{ width: '16px', height: '16px', borderRadius: '4px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', ...(isSel ? { background: '#3d8bff', border: '1.5px solid #3d8bff' } : { border: '1.5px solid var(--muted)' }) }}>
                        {isSel && <Icon name="check" size={13} color="#fff" />}
                      </span>
                    ) : (
                      <span style={{ ...dotStyle(meta.color, 7), boxShadow: '0 0 0 3px ' + meta.color + '1f' }} />
                    )}
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '15.5px',
                        fontWeight: 700,
                        color: 'var(--text)',
                        letterSpacing: '.01em',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {a.name}
                    </span>
                    <span
                      style={{
                        fontSize: '10.5px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '999px',
                        color: meta.color,
                        background: meta.color + '1a',
                        border: '1px solid ' + meta.color + '40',
                        whiteSpace: 'nowrap',
                        flex: 'none',
                      }}
                    >
                      {meta.label}
                    </span>
                    {/* Uzaktan destek. Kartın kendisi temsilci detayına gidiyor, bu yüzden düğmenin
                        olayı DURDURULUR — paneli bozuk bir temsilciyle telefondayken yanlışlıkla
                        sayfa değiştirmek en son istenecek şey. */}
                    {isAdmin && (
                      <span
                        role="button"
                        tabIndex={0}
                        title={a.panelDark
                          ? 'Uzaktan destek — ⚠ panel yanıt vermiyor, komut ULAŞMAYABİLİR'
                          : 'Uzaktan destek'}
                        onClick={(e) => { e.stopPropagation(); setDrawerId(a.id); }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); e.stopPropagation();
                            setDrawerId(a.id);
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', flex: 'none', cursor: 'pointer',
                          padding: '2px 4px', borderRadius: '6px',
                        }}
                      >
                        {/* A dark panel tints the 🛟 amber — the at-a-glance "a command may not land here". */}
                        <Icon name="support_agent" size={16} color={a.panelDark ? 'var(--warn, #e0a23c)' : 'var(--faint)'} />
                      </span>
                    )}
                  </div>
                  {/* 2. satır adın ALTINA hizalanır (paddingLeft = nokta 7px + boşluk 9px).
                      Departman büzülür, vardiya ve marka çipi flex:none ile sabit kalır —
                      başlık kart genişliğinden BAĞIMSIZ olarak her zaman tam iki satır. */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '16px' }}>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11.5px',
                        color: 'var(--dim)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {/* Departman atanmamışsa satır BAŞLAMAZ: koşulsuz basılan boş
                          span, flex gap'i yüzünden vardiyayı sağa kaydırırdı. */}
                      {a.department && (
                        <span title={a.department} style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {a.department}
                        </span>
                      )}
                      {sch && shiftMeta && (
                        <>
                          <Icon
                            name={shiftMeta[3]}
                            size={13}
                            color={sch.inboundDuty ? shiftMeta[0] : '#5f6b7d'}
                            style={{ flex: 'none' }}
                          />
                          {/* Dinamik TR dizgi TEK ifade olarak birleştirilir; i18n çevirisi
                              metin düğümü başınadır (serpiştirme EN'de sızıntı yapar). */}
                          <span
                            title={sch.inboundLabel
                              ? sch.inboundLabel + (sch.task ? ' · ' + sch.task : '')
                              : sch.task || sch.label}
                            style={{ flex: 'none', color: 'var(--faint)', fontVariantNumeric: 'tabular-nums' }}
                          >
                            {sch.startAt && sch.endAt ? sch.startAt + '–' + sch.endAt : sch.label}
                          </span>
                        </>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: '4px', flex: 'none' }}>
                      {a.brands.map((b) => (
                        <span
                          key={b}
                          style={{
                            fontSize: '9.5px',
                            fontWeight: 700,
                            color: 'var(--dim)',
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: '5px',
                            padding: '2px 6px',
                            letterSpacing: '.03em',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {brandLabel(b)}
                        </span>
                      ))}
                      {/* Eklenti sürümü + sağlık (tasarım A) — marka çiplerinin yanında, flex:none: başlık yine
                          tam iki satır (§8). Hiç görülmediyse çip YOK (§6). Gri = sorun yok; amber = eski sürüm ya
                          da bugün uyarı; kırmızı = kayıp sonuç. Üstüne gelince nedenler, tıklayınca (admin) çekmece. */}
                      {extVer && (
                        <span
                          role={isAdmin ? 'button' : undefined}
                          title={chipH.reasons.length ? undefined : 'Eklenti sürümü ' + extVer}
                          onMouseEnter={(e) => {
                            if (!chipH.reasons.length) return;
                            const r = e.currentTarget.getBoundingClientRect();
                            setTip({ id: a.id, x: r.left, y: r.bottom + 6 });
                          }}
                          onMouseLeave={() => setTip(null)}
                          onClick={(e) => {
                            if (!isAdmin || selectMode) return;
                            e.stopPropagation();
                            setTip(null);
                            setDrawerId(a.id);
                          }}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '9.5px',
                            fontWeight: chipC ? 700 : 600,
                            color: chipC || 'var(--faint)',
                            background: chipC ? chipC + '1f' : 'transparent',
                            border: '1px solid ' + (chipC ? chipC + '59' : 'var(--border)'),
                            borderRadius: '5px',
                            padding: '2px 5px',
                            whiteSpace: 'nowrap',
                            fontVariantNumeric: 'tabular-nums',
                            cursor: isAdmin && !selectMode ? 'pointer' : 'default',
                          }}
                        >
                          {chipC && <span style={dotStyle(chipC, 5)} />}
                          {'v' + extVer}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {/* Metrik şeridi: üç sütun (sol/orta/sağ) değer + altında etiket. */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'auto 1fr auto',
                    alignItems: 'end',
                    columnGap: '12px',
                    rowGap: '6px',
                    background: 'var(--inset)',
                    border: '1px solid var(--border)',
                    borderRadius: '11px',
                    padding: '10px 13px 9px',
                  }}
                >
                  <span
                    // Softphone ölçümü yalnız title'da — kart 340×172 sabit (§8), şerit üç sütun kalır.
                    // Anahtar yoksa (eski sürüm: ölçülmedi) title da yok.
                    title={a.dialsToday !== undefined
                      ? 'Arama ' + a.dialsToday + ' · bağlanan ' + (a.connectedToday ?? 0) + ' · konuşma ' + fmtTalkLong(a.talkSecToday ?? 0)
                      : undefined}
                    style={{ fontSize: '19px', fontWeight: 700, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}
                  >
                    {a.todayCalls}
                  </span>
                  <span style={{ textAlign: 'center', fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtRange(a.firstCallAt, a.lastCallAt)}
                  </span>
                  <span style={{ textAlign: 'right', fontSize: '12.5px', fontWeight: 600, color: 'var(--text)', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                    {a.terminalOpenAt}
                  </span>
                  <span style={{ ...METRIC_LABEL }}>ÇAĞRI</span>
                  <span style={{ ...METRIC_LABEL, textAlign: 'center' }}>İLK–SON</span>
                  <span style={{ ...METRIC_LABEL, textAlign: 'right' }}>TERMİNAL</span>
                </div>
                {/* TEK alt satır, YEDİ varyant (mola > aranıyor > aramada > müşteri işlemde >
                    gelen çağrı > izinli > son aktivite). Mola/izin/çağrı bilgisi ayrı bir şerit
                    DEĞİL — bu sayede her kart aynı yükseklikte kalır (§8). */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    fontSize: '11.5px',
                    color: 'var(--faint)',
                    borderTop: '1px solid var(--border)',
                    paddingTop: '9px',
                  }}
                >
                  {isBreak ? (
                    <>
                      <Icon name="local_cafe" size={16} color={over ? '#ef6d6d' : '#e0a23c'} style={{ flex: 'none' }} />
                      <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>
                        {a.breakStartedAt ? 'Mola ' + fmtClock(a.breakStartedAt, tz) : 'Mola'}
                      </span>
                      <span style={{ flex: 'none' }}>·</span>
                      <span style={{ fontWeight: 600, whiteSpace: 'nowrap', color: over ? 'var(--bad-text)' : '#e0a23c' }}>
                        {over ? 'Süre aşıldı' : a.breakRemainingMin + ' dk kaldı'}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', fontWeight: 700, lineHeight: 1, color: over ? '#ef6d6d' : '#e0a23c' }}>
                          {fmtElapsed(a.breakStartedAt, now)}
                        </span>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : a.onCall && a.onCall.phase === 'ringing' ? (
                    /* ÖLÇÜLEN çağrı, çalıyor (softphone call_sessions). Sayaç YOK: çalma süresi
                       süpervizöre bir şey söylemez ve 2 dk'da kendiliğinden düşer. */
                    <>
                      <Icon name="ring_volume" size={16} color="#5b8fd6" style={{ flex: 'none' }} />
                      <span style={{ whiteSpace: 'nowrap', color: 'var(--dim)' }}>Aranıyor…</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : a.onCall ? (
                    /* ÖLÇÜLEN çağrı, konuşuyor. Sayaç cevaplandığı andan (sunucu saati); "Müşteri
                       işlemde"den ÖNCE gelir — görüşme sürerken müşteri zaten açıktır. */
                    <>
                      <Icon name="call" size={16} color="#37c46b" style={{ flex: 'none' }} />
                      <span style={{ whiteSpace: 'nowrap', color: 'var(--dim)' }}>Aramada</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', fontWeight: 700, lineHeight: 1, color: '#37c46b' }}>
                          {fmtElapsed(onCallClock(a.onCall, now), now)}
                        </span>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : a.handlingSince ? (
                    /* Müşteri işlemede — eklentinin `AwaitingOutcome` durumu. Molanın
                       ARDINDAN geliyor: mola basılmışsa onu maskeleyemez. Süre sağda,
                       molanınkiyle aynı yuvada; ayrı bir şerit yok, yükseklik sabit.
                       Etiket iki nokta ALMAZ: değeri sağa yaslı olduğu için yanına
                       hiçbir şey gelmiyor, sarkık bir iki nokta kalırdı. */
                    <>
                      <Icon
                        name={handlingIdle ? 'hourglass_empty' : 'support_agent'}
                        size={16}
                        color={handlingIdle ? '#e0a23c' : '#5b8fd6'}
                        style={{ flex: 'none' }}
                      />
                      <span style={{ whiteSpace: 'nowrap', color: handlingIdle ? 'var(--dim)' : undefined }}>
                        {handlingIdle ? 'Müşteri açık, işlem yok' : 'Müşteri işlemde'}
                      </span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: '13.5px', fontWeight: 700, lineHeight: 1, color: handlingIdle ? '#e0a23c' : 'var(--accent-text)' }}>
                          {fmtElapsed(a.handlingSince, now)}
                        </span>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : sch?.inboundActive ? (
                    /* Gelen çağrı görevi: santralden gelen çağrılar eklentiye UĞRAMAZ,
                       yani kart doğru çalışan bir temsilci için "Aktivite yok" derdi —
                       tam da motorun uyarı üretmediği aralık. Aynı kaynağı okur
                       (schedule.inboundActive), böylece kart ile motor ayrışamaz. */
                    <>
                      <Icon name="call_received" size={16} color="#2fb8a8" style={{ flex: 'none' }} />
                      <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>{sch.inboundLabel}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : offDuty && sch ? (
                    /* İzinli/TL satırı NÖTR: plan dışı çalışma artık vurgulanmıyor
                       (sahibin kararı, 2026-07-27 — backend eşi de kaldırıldı, bkz.
                       engine/status-map.js RETIRED_ALERT_RULES). TL'nin her gün çağrı
                       yapması normal, izin gününde devreye girmek de öyle; kartta amber
                       bir uyarı olarak okunması yanlıştı. */
                    <>
                      <Icon
                        name={shiftMeta ? shiftMeta[3] : 'event_busy'}
                        size={16}
                        color="var(--faint)"
                        style={{ flex: 'none' }}
                      />
                      <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>{sch.label}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon name="schedule" size={14} style={{ flex: 'none' }} />
                      {/* Hiç görülmemişse etiket de düşer: yer tutucu olmadığı için
                          "Son aktivite:" tek başına sarkık bir iki nokta bırakırdı. */}
                      {a.lastSeenAt ? (
                        <>
                          <span style={{ whiteSpace: 'nowrap' }}>Son aktivite:</span>
                          <span style={{ color: 'var(--dim)', whiteSpace: 'nowrap' }}>{fmtRel(a.lastSeenAt, now)}</span>
                        </>
                      ) : (
                        <span style={{ whiteSpace: 'nowrap' }}>Aktivite yok</span>
                      )}
                      <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '9px', flex: 'none' }}>
                        <Icon name="chevron_right" size={17} color="var(--muted)" />
                      </span>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toplu işlem çubuğu sabit ve alttadır — son kart sırası altında kalmasın. */}
      {selectMode && isAdmin && <div style={{ height: '96px' }} />}

      {tip && tipHealth && tipHealth.reasons.length > 0 && (
        <div
          style={{
            position: 'fixed', left: Math.max(8, Math.min(tip.x - 120, window.innerWidth - 290)) + 'px', top: tip.y + 'px',
            width: '270px', zIndex: 85, pointerEvents: 'none', background: 'var(--surface2)', border: '1px solid var(--border2)',
            borderRadius: '11px', padding: '11px 13px', boxShadow: '0 14px 34px rgba(0,0,0,.45)',
            display: 'flex', flexDirection: 'column', gap: '7px',
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '.06em', color: tipHealth.level === 'bad' ? '#ef5d5d' : '#e0a23c' }}>BAKILMALI</div>
          {tipHealth.reasons.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', fontSize: '12px', color: 'var(--text)', lineHeight: 1.4 }}>
              <span style={{ ...dotStyle(r.level === 'bad' ? '#ef5d5d' : '#e0a23c', 7), marginTop: '5px' }} />
              <span>{r.text}</span>
            </div>
          ))}
          {isAdmin && (
            <div style={{ borderTop: '1px solid var(--border2)', paddingTop: '7px', fontSize: '11.5px', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span>Eklenti Sağlığını açmak için tıklayın</span>
              <Icon name="arrow_forward" size={15} />
            </div>
          )}
        </div>
      )}

      {drawerAgent && isAdmin && (
        <ExtHealthDrawer
          agent={drawerAgent}
          newest={newestExt}
          outdated={isOutdated(drawerAgent.extVersion || '', newestExt)}
          lang={lang}
          onClose={() => setDrawerId(null)}
          onOpenAllCommands={() => setCmdTarget({ id: drawerAgent.id, name: drawerAgent.name, panelDark: drawerAgent.panelDark, lastBeatAt: drawerAgent.lastBeatAt })}
        />
      )}

      {selectMode && isAdmin && (
        // Only the selected cards still ON SCREEN: a selection made under another brand / department / search
        // filter used to ride along invisibly ("5 seçili" while 3 of them were filtered out).
        <BulkBar agents={liveRows.filter((x) => selected.has(x.id))} lang={lang} onClear={() => setSelected(new Set())} />
      )}

      {cmdTarget && (
        <AgentCommandModal
          agentId={cmdTarget.id}
          agentName={cmdTarget.name}
          panelDark={cmdTarget.panelDark}
          lastBeatAt={cmdTarget.lastBeatAt}
          onClose={() => setCmdTarget(null)}
        />
      )}
    </>
  );
}
